/**
 * Patients list UI store — Zustand.
 *
 * Owns only the list-screen filter toggles (showArchived / showMerged).
 * Persists per-user under localStorage, mirroring the scheduling store's
 * convention (`scrips.scheduling.<userId>`). Resets to defaults on user
 * switch so user A's filters never leak into user B's session.
 *
 * Server state (patient rows) lives in React Query — never here.
 */
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useAuthStore } from '@/auth/auth-store';

interface PatientsListState {
  showArchived: boolean;
  showMerged: boolean;

  setShowArchived: (v: boolean) => void;
  setShowMerged: (v: boolean) => void;
  clearFilters: () => void;
}

const ANON_USER_ID = 'anonymous';
const currentUserKeySuffix = () =>
  useAuthStore.getState().user?.userId ?? ANON_USER_ID;

export const usePatientsListStore = create<PatientsListState>()(
  persist(
    (set) => ({
      showArchived: false,
      showMerged: false,
      setShowArchived: (v) => set({ showArchived: v }),
      setShowMerged: (v) => set({ showMerged: v }),
      clearFilters: () => set({ showArchived: false, showMerged: false }),
    }),
    {
      name: 'scrips.patients',
      // Per-user key suffix — mirrors Flutter's `key + userId` SharedPreferences
      // convention. createJSONStorage handles serialize/deserialize so we
      // only own the raw string getter/setter/remover.
      storage: createJSONStorage(() => ({
        getItem: (name) => {
          if (typeof window === 'undefined') return null;
          return localStorage.getItem(`${name}.${currentUserKeySuffix()}`);
        },
        setItem: (name, value) => {
          if (typeof window === 'undefined') return;
          localStorage.setItem(`${name}.${currentUserKeySuffix()}`, value);
        },
        removeItem: (name) => {
          if (typeof window === 'undefined') return;
          localStorage.removeItem(`${name}.${currentUserKeySuffix()}`);
        },
      })),
      partialize: (state): Pick<PatientsListState, 'showArchived' | 'showMerged'> => ({
        showArchived: state.showArchived,
        showMerged: state.showMerged,
      }),
    },
  ),
);

// Auth-aware rehydration — reset + rehydrate when the userId changes.
if (typeof window !== 'undefined') {
  let lastUserId: string | undefined = useAuthStore.getState().user?.userId;
  useAuthStore.subscribe((state) => {
    const newUserId = state.user?.userId;
    if (newUserId === lastUserId) return;
    lastUserId = newUserId;
    usePatientsListStore.setState({ showArchived: false, showMerged: false });
    void usePatientsListStore.persist.rehydrate();
  });
}
